import React, { useState, useEffect } from 'react';
import { Shield, TrendingUp, Users, AlertTriangle, DollarSign, Activity, Lock, Eye, BarChart3, Globe, Zap, Settings, Database, Clock, CheckCircle } from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [systemStatus, setSystemStatus] = useState('operational');
  const [realTimeData, setRealTimeData] = useState({
    treasury: 7.2,
    members: 247,
    uptime: 99.97,
    security: 98.5
  });

  const systemMetrics = [
    { 
      label: 'Treasury Value', 
      value: `${realTimeData.treasury}M SUI`, 
      status: 'healthy', 
      icon: DollarSign,
      change: '+2.3%',
      trend: 'up'
    },
    { 
      label: 'Platform Uptime', 
      value: `${realTimeData.uptime}%`, 
      status: 'healthy', 
      icon: Activity,
      change: '+0.1%',
      trend: 'up'
    },
    { 
      label: 'Active Members', 
      value: realTimeData.members.toString(), 
      status: 'healthy', 
      icon: Users,
      change: '+12',
      trend: 'up'
    },
    { 
      label: 'Security Score', 
      value: `${realTimeData.security}%`, 
      status: 'healthy', 
      icon: Shield,
      change: '+1.2%',
      trend: 'up'
    },
  ];

  const alerts = [
    { 
      id: 1,
      type: 'warning', 
      title: 'Treasury Rebalancing',
      message: 'Treasury allocation needs rebalancing to optimize yield', 
      time: '2 hours ago',
      priority: 'high',
      action: 'Rebalance Now'
    },
    { 
      id: 2,
      type: 'info', 
      title: 'New Governance Proposal',
      message: 'Proposal "Acquire Thunder Bay for $2.5M" submitted for review', 
      time: '4 hours ago',
      priority: 'medium',
      action: 'Review Proposal'
    },
    { 
      id: 3,
      type: 'success', 
      title: 'Horse Acquisition Complete',
      message: 'Thunder Bay acquisition completed successfully', 
      time: '1 day ago',
      priority: 'low',
      action: 'View Details'
    },
    {
      id: 4,
      type: 'error',
      title: 'Security Alert',
      message: 'Unusual activity detected in governance voting',
      time: '3 hours ago',
      priority: 'critical',
      action: 'Investigate'
    }
  ];

  const quickActions = [
    { name: 'Rebalance Treasury', icon: DollarSign, color: 'cyan', action: () => console.log('Rebalance') },
    { name: 'Review Proposals', icon: Activity, color: 'blue', action: () => console.log('Review') },
    { name: 'System Settings', icon: Settings, color: 'purple', action: () => console.log('Settings') },
    { name: 'Security Center', icon: Shield, color: 'red', action: () => console.log('Security') },
    { name: 'Analytics Report', icon: BarChart3, color: 'green', action: () => console.log('Analytics') },
    { name: 'Database Backup', icon: Database, color: 'yellow', action: () => console.log('Backup') }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'treasury', label: 'Treasury', icon: DollarSign },
    { id: 'governance', label: 'Governance', icon: Activity },
    { id: 'system', label: 'System', icon: Settings }
  ];

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeData(prev => ({
        treasury: prev.treasury + (Math.random() - 0.5) * 0.1,
        members: prev.members + Math.floor((Math.random() - 0.5) * 2),
        uptime: Math.min(99.99, prev.uptime + (Math.random() - 0.5) * 0.01),
        security: Math.min(100, prev.security + (Math.random() - 0.5) * 0.5)
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'error': return 'border-red-500/30 bg-red-500/10';
      case 'warning': return 'border-yellow-500/30 bg-yellow-500/10';
      case 'success': return 'border-green-500/30 bg-green-500/10';
      default: return 'border-blue-500/30 bg-blue-500/10';
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'success': return '✅';
      default: return 'ℹ️';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-400 bg-red-500/20';
      case 'high': return 'text-yellow-400 bg-yellow-500/20';
      case 'medium': return 'text-blue-400 bg-blue-500/20';
      default: return 'text-green-400 bg-green-500/20';
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
              <p className="text-slate-400">Comprehensive system management and monitoring</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-medium">All Systems Operational</span>
              </div>
              <button
                onClick={() => window.location.href = '/admin/verify'}
                className="flex items-center space-x-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors"
              >
                <Lock className="w-4 h-4" />
                <span>Security Center</span>
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-slate-700/50 rounded-lg p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-cyan-500 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* System Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {systemMetrics.map((metric, index) => (
            <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <metric.icon className="w-8 h-8 text-cyan-400" />
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    metric.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <span className={`text-xs font-medium ${
                    metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {metric.change}
                  </span>
                </div>
              </div>
              <div className="text-2xl font-bold text-white mb-1">{metric.value}</div>
              <div className="text-slate-400 text-sm">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Alerts */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">System Alerts</h2>
                  <span className="text-slate-400 text-sm">{alerts.length} active alerts</span>
                </div>
                <div className="space-y-4">
                  {alerts.map((alert) => (
                    <div key={alert.id} className={`p-4 rounded-lg border ${getAlertColor(alert.type)}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <span className="text-xl">{getAlertIcon(alert.type)}</span>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="text-white font-medium">{alert.title}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(alert.priority)}`}>
                                {alert.priority}
                              </span>
                            </div>
                            <p className="text-slate-400 text-sm mb-2">{alert.message}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500 text-xs">{alert.time}</span>
                              <button className="text-cyan-400 hover:text-cyan-300 text-sm font-medium">
                                {alert.action} →
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h2 className="text-xl font-bold text-white mb-6">Quick Actions</h2>
                <div className="grid grid-cols-2 gap-4">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={action.action}
                      className={`p-4 rounded-lg border border-slate-600 hover:border-${action.color}-500/50 transition-all duration-200 group`}
                    >
                      <action.icon className={`w-6 h-6 text-${action.color}-400 mb-2 group-hover:text-${action.color}-300`} />
                      <div className="text-white text-sm font-medium">{action.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-8">
            {/* Security Overview */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Security Status</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Firewall Status</span>
                    <span className="text-green-400 font-medium">Active</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">DDoS Protection</span>
                    <span className="text-green-400 font-medium">Enabled</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">SSL Certificate</span>
                    <span className="text-green-400 font-medium">Valid</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Last Security Scan</span>
                    <span className="text-slate-400">2 hours ago</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Access Logs</h3>
                <div className="space-y-3">
                  {[
                    { user: 'Admin', action: 'Login', time: '2 min ago', status: 'success' },
                    { user: 'System', action: 'Backup', time: '1 hour ago', status: 'success' },
                    { user: 'Admin', action: 'Settings Change', time: '3 hours ago', status: 'success' },
                    { user: 'Unknown', action: 'Failed Login', time: '4 hours ago', status: 'failed' }
                  ].map((log, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div>
                        <div className="text-white text-sm font-medium">{log.user}</div>
                        <div className="text-slate-400 text-xs">{log.action}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-medium ${
                          log.status === 'success' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {log.status}
                        </div>
                        <div className="text-slate-500 text-xs">{log.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Threat Detection</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-400 mb-2">0</div>
                    <div className="text-slate-400 text-sm">Active Threats</div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Blocked Attempts (24h)</span>
                      <span className="text-white">12</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Suspicious Activity</span>
                      <span className="text-yellow-400">2</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Security Score</span>
                      <span className="text-green-400">98.5%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'treasury' && (
          <div className="space-y-8">
            {/* Treasury Management */}
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Treasury Allocation</h3>
                <div className="space-y-4">
                  {[
                    { asset: 'SUI Staked', amount: '4.2M', percentage: 58, color: 'cyan' },
                    { asset: 'USDC Reserve', amount: '1.8M', percentage: 25, color: 'blue' },
                    { asset: 'Horse Assets', amount: '1.2M', percentage: 17, color: 'green' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 bg-${item.color}-500 rounded-full`}></div>
                        <span className="text-white font-medium">{item.asset}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-bold">{item.amount}</div>
                        <div className="text-slate-400 text-sm">{item.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Yield Performance</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-cyan-400 mb-2">15.7%</div>
                    <div className="text-slate-400 text-sm">Current APY</div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Daily Yield</span>
                      <span className="text-white">$2,847</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Monthly Yield</span>
                      <span className="text-white">$86,500</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Annual Projection</span>
                      <span className="text-white">$1.1M</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'governance' && (
          <div className="space-y-8">
            {/* Governance Overview */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Proposal Stats</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-slate-300">Total Proposals</span>
                    <span className="text-white font-bold">24</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">Active Proposals</span>
                    <span className="text-cyan-400 font-bold">3</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">Passed Proposals</span>
                    <span className="text-green-400 font-bold">18</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">Rejected Proposals</span>
                    <span className="text-red-400 font-bold">3</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Voting Activity</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-cyan-400 mb-2">89.3%</div>
                    <div className="text-slate-400 text-sm">Average Participation</div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">High Participation</span>
                      <span className="text-green-400">15 proposals</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Medium Participation</span>
                      <span className="text-yellow-400">6 proposals</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Low Participation</span>
                      <span className="text-red-400">3 proposals</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {[
                    { action: 'New Proposal Created', time: '2 hours ago', user: 'Member #247' },
                    { action: 'Vote Cast', time: '4 hours ago', user: 'Member #156' },
                    { action: 'Proposal Passed', time: '1 day ago', user: 'System' },
                    { action: 'Governance Updated', time: '2 days ago', user: 'Admin' }
                  ].map((activity, index) => (
                    <div key={index} className="p-3 bg-slate-700/50 rounded-lg">
                      <div className="text-white text-sm font-medium">{activity.action}</div>
                      <div className="text-slate-400 text-xs mt-1">
                        {activity.user} • {activity.time}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="space-y-8">
            {/* System Health */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">System Performance</h3>
                <div className="space-y-4">
                  {[
                    { metric: 'CPU Usage', value: '23%', status: 'good' },
                    { metric: 'Memory Usage', value: '67%', status: 'good' },
                    { metric: 'Disk Usage', value: '45%', status: 'good' },
                    { metric: 'Network Latency', value: '12ms', status: 'excellent' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-slate-300">{item.metric}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-medium">{item.value}</span>
                        <div className={`w-2 h-2 rounded-full ${
                          item.status === 'excellent' ? 'bg-green-500' :
                          item.status === 'good' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Database Status</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Connection Pool</span>
                    <span className="text-green-400 font-medium">Healthy</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Query Performance</span>
                    <span className="text-green-400 font-medium">Optimal</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Last Backup</span>
                    <span className="text-slate-400">2 hours ago</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Storage Used</span>
                    <span className="text-white">2.3GB / 10GB</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;